/**
 * ChatResponseAppointmentList component for the Indigo design system.
 *
 * Displays a header (often containing an inline physician pill via
 * {@link ChatInlineElement}) followed by a stack of {@link ChatResponseAppointment}
 * rows separated by hairlines.
 *
 * @example
 * ```tsx
 * <ChatResponseAppointmentList
 *   header={<>Upcoming appointments with <ChatInlineElement type="physician" label="Dr. Annette Zhu" /></>}
 *   appointments={[
 *     { id: "1", title: "Follow-up", subtitle: "Tue, Mar 12 · 9:00 AM", status: { label: "Confirmed", variant: "done" } },
 *     { id: "2", title: "Records review", subtitle: "Mon, Mar 11 · 2:30 PM", status: { label: "Needs action", variant: "todo" } },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, ReactNode } from "react";
import { Body } from "../../Typography/Body";
import {
  ChatResponseAppointment,
  ChatResponseAppointmentProps,
} from "../ChatResponseAppointment";
import "./ChatResponseAppointmentList.css";

export interface ChatResponseAppointmentListItem
  extends Omit<ChatResponseAppointmentProps, "className"> {
  /** Stable identifier used as React key. */
  id: string;
}

export interface ChatResponseAppointmentListProps {
  /**
   * Header rendered above the appointment list. Accepts a string or any
   * ReactNode so callers can embed inline pills (e.g. `ChatInlineElement`).
   */
  header?: ReactNode;
  /** List of appointments. */
  appointments: ChatResponseAppointmentListItem[];
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatResponseAppointmentList = forwardRef<
  HTMLDivElement,
  ChatResponseAppointmentListProps
>(({ header, appointments, className, "data-testid": dataTestId }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx("indigo-chat-response-appointment-list", className)}
      data-testid={dataTestId}
    >
      {header && (
        <Body
          as="div"
          size="sm"
          className="indigo-chat-response-appointment-list__header"
        >
          {header}
        </Body>
      )}
      <div className="indigo-chat-response-appointment-list__list" role="list">
        {appointments.map(({ id, ...rest }) => (
          <div
            key={id}
            role="listitem"
            className="indigo-chat-response-appointment-list__row"
          >
            <ChatResponseAppointment
              {...rest}
              className="indigo-chat-response-appointment-list__item"
            />
          </div>
        ))}
      </div>
    </div>
  );
});

ChatResponseAppointmentList.displayName = "ChatResponseAppointmentList";

export default ChatResponseAppointmentList;
